import { makeStyles } from '@material-ui/styles';
import { Autocomplete } from '@material-ui/lab';
import useSessionStore from '@services/stores/SessionStore';
import { useEffect, useState } from 'react';
import { useTranslation } from '@tools/i18n';
import { InputAdornment, TextField } from '@material-ui/core';
import { ucfirst } from '@services/Utils';
import theme from '@components/theme';
//@ts-ignore
import Flags from "react-phone-number-input/modules/Flag";

const useStyles = makeStyles({

    input: {
        height: '100%',
        '&[aria-expanded=true] .embedded input': {
            width: '200px',
            maxWidth: 'calc(50vw - 60px) !important',
            //@ts-ignore
            [theme.breakpoints.up('md')]: {
                width: '100%',
                maxWidth: 'calc(100vw - 60px) !important',
            }
        },

        '& .MuiFormControl-root': {
            height: '100%'
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '2px',
            border: '1px solid #041e3a63',
            outline: 0,
        },
        '& .MuiOutlinedInput-root': {
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            flexWrap: 'nowrap',
        },
        '&.has-errors .MuiOutlinedInput-notchedOutline2': {
            borderColor: theme.palette.primary.darker,
        },
        '& .MuiInputAdornment-root': {
            //transform: 'translate(15px, 28px)',
            marginRight: 0,
            marginLeft: '2px',
        },
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input': {
            padding: '6px ',
        },
        '& .MuiAutocomplete-clearIndicator': {
            width: 0,
            display: 'none'
        }

    },
    label: {
        fontSize: 12,
        fontFamily: theme.typography.fontFamily,
    },
    inputWrapper: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        position: 'relative',
        '& .MuiInputLabel-outlined': {
            top: '-4px'
        },
        '& .embedded > div': {
            padding: 0,
        },
        '& .embedded > div > input': {
            padding: '0 !important',
            width: 0,
            minWidth: 0,
            margin: 0,
        },
        '& .embedded > div > .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            padding: 0,
        },
        '& .MuiAutocomplete-popper': {
            minWidth: '200px !important',
            width: 'fit-content !important',
            maxWidth: 'calc(100vw - 60px) !important',
            position: 'absolute',
            top: '100%',
            left: '0',
            '& .MuiAutocomplete-option': {

                width: '100%',
                overflowX: 'hidden',
                paddingRight: 0,
            }
        },
        '& .embedded + .MuiAutocomplete-popper': {
            top: '35px',
            left: '-14px',
        },
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            paddingRight: '25px !important',
        },
        '& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            paddingRight: '25px !important',
        }

    },
    errors: {
        display: 'grid',
        padding: '6px 8px',
        color: '#ffffff',
        gap: 4,
        fontSize: 12,
        background: theme.palette.primary.darker,
    },
}, { name: 'np-country-selector' });

interface NewportCountrySelectorProps {
    onChange: (countryCode: CountryOption | null) => void;
    initValue?: string;
    label?: null | Label;
    optionSize?: 'none' | 'code' | 'name' | 'full' | null;
    optionFlag?: boolean;
    embedded?: boolean;
    errors?: any[];
}

interface CountryOption {
    code: string;
    label: string;
    id: string;
}

interface Label {
    mode: 'inline' | 'above';
    value: string;
}

export default function NewportCountrySelector(
    {
        onChange,
        initValue = '',
        label = null,
        optionSize = null,
        optionFlag = false,
        embedded = false,
        errors = [],
    }: NewportCountrySelectorProps,
) {
    const classes = useStyles();
    const sessionMethods = useSessionStore((state) => state.methods);
    const countries = useSessionStore((state) => state.session.countries);
    const { t } = useTranslation();
    const autoCountryCode = useSessionStore((state) => state.session.geoIpCountry);
    const [id, setId] = useState(
        `np-input-${Math.ceil((Date.now() / 1000) * Math.random()) + Math.floor(Math.random() * 100000)}`,
    );
    const [value, setValue] = useState<CountryOption | null>(null);
    const [options, setOptions] = useState<CountryOption[]>([]);

    useEffect(() => {
        if (!value) {
            sessionMethods.getCountries().then((countries) => {
                const options = countries.map((country) => {
                    return {
                        code: country.iso || '',
                        label: country.translated.name || country.name || '',
                        id: country.id || ''
                    };
                }).sort((a, b) => a.label.localeCompare(b.label, ['sv', 'en'], { sensitivity: 'case' }));

                setOptions(options);

                if (options.length === 1) {
                    const autoVal = options[0];
                    setValue(autoVal);
                } else {
                    const auto = options.find(option => option.code === autoCountryCode) || null;
                    setValue(auto);
                }
            });
        }
    }, [countries]);

    useEffect(() => {
        if (onChange) {
            onChange(value);
        }
    }, [value]);


    function renderLabel(label: Label | null) {
        if (!label || label?.mode === 'inline') {
            return null;
        }

        return <>
            <label className={classes.label} htmlFor={id}>
                {label?.value}
            </label>
        </>;
    }

    function validation(errors: string[]) {
        if (!Array.isArray(errors) || errors.length <= 0) {
            return null;
        }

        return <div className={classes.errors}>
            {errors.map((error) => {
                return <span>{ucfirst(error)}</span>;
            })}
        </div>;
    }

    function errorClass() {
        return errors?.length > 0 ? 'has-errors' : null;
    }

    function getFlag(countryCode: string) {
        return <Flags
            country={countryCode !== 'GB' ? countryCode.toUpperCase() : 'GBR'}
            countryName={countryCode !== 'GB' ? countryCode.toUpperCase() : 'GBR'}
            flagUrl={'/images/flags/s/{XX}.svg'}
            style={{ width: 20, marginRight: 8 }}
            key={'flags-key-' + countryCode}
        />
    }

    // @ts-ignore
    return (
        <div className={classes.inputWrapper}>
            {renderLabel(label)}
            <Autocomplete
                noOptionsText={'No match found'}
                disablePortal
                disableClearable={value !== null}
                className={[classes.input, errorClass(), embedded ? 'embedded': ''].join(' ')}
                renderInput={params => {
                    return (
                        <>
                            <TextField
                                {...params}
                                variant={'outlined'}
                                className={embedded ? 'embedded': ''}
                                label={label?.mode === 'inline' ? label?.value : ''}
                                id={id}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        optionFlag &&
                                        <InputAdornment position="start">{getFlag(value?.code || initValue || '')}</InputAdornment>
                                    )
                                }}
                                InputLabelProps={{
                                    //@ts-ignore
                                    shrink: !!value || params.InputProps?.startAdornment != null || params?.InputProps?.focused
                                }}
                            />
                        </>
                    );
                    }
                }
                renderOption={(props, option) => {
                    let text = props?.label;
                    if (optionSize) {
                        switch (optionSize) {
                            case 'code':
                                text = `${props?.label}`;
                                break;
                            case 'name':
                                text = `${props?.label}`;
                                break;
                            case 'full':
                                text = `${props?.label} ${props?.code}`;
                                break;
                        }
                    }
                    return (
                        <>
                            {optionFlag ? getFlag(props.code) : ''}
                            {text}
                        </>
                    );
                }}
                getOptionLabel={option => {
                    let text = option?.label;
                    if (optionSize) {
                        switch (optionSize) {
                            case 'none':
                                text = ``;
                                break;
                            case 'code':
                                text = `${option?.code}`;
                                break;
                            case 'name':
                                text = `${option?.label}`;
                                break;
                            case 'full':
                                text = `${option?.label} ${option?.code}`;
                                break;
                        }
                    }
                    return text;
                }}
                options={options}
                filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                    )
                }
                value={value}
                onChange={(event, value, reason) => {
                    setValue(value);
                }}
            />
            {validation(errors)}
        </div>
    );
}
